// KreditVyspravky.jsx — vyspravky_v1 (20. 10. 2026, Mirko, Kronika zakazky 2084–2252). Jeden komponent pre celý Matrix (0.J):
//   PercentoVyplaty      — výmera na výplatu partiám: percento na zákazke, zapína sa raz do konca zákazky (Súpis › Podklady aj Výdavky)
//   KreditVyspravky      — Výdavky zákazky › Kredit na výspravky (vytvorené, čerpané, uvoľnené, zostáva) — len office a vedenie
//   PodkladPartiaTlacidlo — Výmery › Podklad pre partiu (partia + mesiac, ako blok v Mzdách)
//   stiahniPodkladPartia — PDF podkladu pre partiu z bloku v Mzdách
// Vzhľad 61.10: Sekcia, KpiRow/KpiCard, Table; farby len z theme.js.
import React, { useEffect, useState } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'
import { Sekcia, KpiRow, KpiCard } from './Ui.jsx'
import { Btn, BtnNav, BtnGhost } from './Btn.jsx'
import { Modal } from './Modal.jsx'
import { Table, THead, TBody, TR, TH, TD } from './Table.jsx'
import { useToast } from './Toast.jsx'
import { useConfirm } from './ConfirmDialog.jsx'
import { usePinGate } from '../hooks/usePinGate.jsx'
import { COLORS, TYPO, SPACE, SK } from '../theme.js'

const API = '/api/vyspravky-kredit'
const PREDVOLBY = [1, 2, 3, 5]
const MES = ['', 'január', 'február', 'marec', 'apríl', 'máj', 'jún', 'júl', 'august', 'september', 'október', 'november', 'december']
const ymTxt = (ym) => { const a = String(ym || '').split('-'); return a.length === 2 ? (MES[+a[1]] || '') + ' ' + a[0] : '' }
const datTxt = (d) => { const a = String(d || '').slice(0, 10).split('-'); return a.length === 3 ? `${+a[2]}. ${+a[1]}. ${a[0]}` : '' }
const pctTxt = (p) => '−' + String(p).replace('.', ',') + ' %'
const cis = (x) => { const v = parseFloat(String(x ?? '').replace(',', '.')); return isFinite(v) ? v : 0 }

function useKredit(projectId) {
  const [k, setK] = useState(null)
  const [zakaz, setZakaz] = useState(false)
  const nacitaj = () => {
    if (!projectId) return
    fetchAuth(`${API}/${encodeURIComponent(projectId)}`)
      .then(r => { if (r.status === 403) { setZakaz(true); return null } return r.ok ? r.json() : null })
      .then(j => { if (j) setK(j) })
      .catch(() => {})
  }
  useEffect(() => { setK(null); setZakaz(false); nacitaj() }, [projectId])   // eslint-disable-line
  return [k, nacitaj, zakaz]
}

export async function stiahniPodkladPartia(blokId, toast) {
  const r = await fetchAuth(`${API}/podklad/${encodeURIComponent(blokId)}/pdf`)
  if (!r.ok) { const d = await r.json().catch(() => ({})); toast && toast.error(d.error || 'Podklad sa nepodarilo vytvoriť'); return }
  const blob = await r.blob()
  const cd = r.headers.get('Content-Disposition') || ''
  const m = cd.match(/filename="([^"]+)"/)
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = (m && m[1]) || 'Podklad_vymery_partia.pdf'
  document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 1500)
}

// ── Výmera na výplatu partiám (percento) ──────────────────────────────
export function PercentoVyplaty({ projectId, onZmena }) {
  const [k, nacitaj, zakaz] = useKredit(projectId)
  const [volba, setVolba] = useState(2)
  const [vlastne, setVlastne] = useState('')
  const [menim, setMenim] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()
  const pinGate = usePinGate()
  if (zakaz || !k) return null
  const p = vlastne.trim() ? cis(vlastne) : volba
  const platne = p > 0 && p <= 10
  const zapni = async () => {
    if (!platne) { toast.warn('Percento musí byť od 0 do 10.'); return }
    const ok = await confirm({ title: `Výmera na výplatu partiám ${pctTxt(p)}?`, confirmLabel: 'Zapnúť do konca zákazky',
      message: `Do Miezd pôjde ${String(100 - p).replace('.', ',')} % výmery pre všetky partie na tejto zákazke — aj v otvorených blokoch, ktoré už v Mzdách sú (uzavreté sa nemenia). Rozdiel pôjde do výdavkov stavby ako kredit na výspravky. V Mzdách, na výplatnom liste ani v podklade pre partiu sa percento neukáže. Percento sa uzamkne do konca zákazky — zmeniť ho môže už len Mirko svojím PIN-om.` })
    if (!ok) return
    const r = await fetchAuth(`${API}/${encodeURIComponent(projectId)}/percento`, { method: 'POST', body: JSON.stringify({ pct: p }) })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) { toast.error(d.error || 'Nepodarilo sa'); return }
    toast.success(`Zapnuté ${pctTxt(p)} do konca zákazky.`); nacitaj(); onZmena && onZmena()
  }
  const zmen = async () => {
    if (!(p >= 0 && p <= 10)) { toast.warn('Percento musí byť od 0 do 10.'); return }
    const v = await confirm({ title: `Zmeniť percento na ${p ? pctTxt(p) : '100 %'}?`, confirmLabel: 'Ďalej — PIN Mirka',
      message: 'Percento je uzamknuté do konca zákazky. Zmenu schváli len Mirko svojím PIN-om; otvorené bloky v Mzdách sa prepočítajú, uzavreté nie.',
      dovod: { label: 'Dôvod zmeny', placeholder: 'napr. omyl pri zadaní', povinne: true } })
    if (!v) return
    const dovod = typeof v === 'string' ? v : (v && v.dovod) || ''
    const g = await pinGate('vyspravky_percento', { info: `Výmera na výplatu partiám ${pctTxt(k.pct)} → ${p ? pctTxt(p) : '100 %'} (${projectId})` })
    if (!g || !g.ok) return
    const r = await fetchAuth(`${API}/${encodeURIComponent(projectId)}/percento`, { method: 'POST', headers: { 'X-Pin-Audit-Id': String(g.audit_id || '') }, body: JSON.stringify({ pct: p, dovod }) })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) { toast.error(d.error || 'Nepodarilo sa'); return }
    toast.success('Percento zmenené.'); setMenim(false); nacitaj(); onZmena && onZmena()
  }
  const vyber = (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
      <div style={{ display: 'inline-flex', border: `1px solid ${COLORS.line}`, borderRadius: 6, overflow: 'hidden' }}>
        {PREDVOLBY.map(v => (
          <button key={v} type="button" onClick={() => { setVolba(v); setVlastne('') }}
            style={{ border: 0, borderRight: `1px solid ${COLORS.line}`, padding: '6px 11px', font: 'inherit', fontWeight: TYPO.weights.semibold, cursor: 'pointer',
              background: !vlastne.trim() && volba === v ? COLORS.nav : COLORS.bg, color: !vlastne.trim() && volba === v ? COLORS.bg : COLORS.text }}>{pctTxt(v)}</button>
        ))}
      </div>
      <span style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>vlastné</span>
      <input value={vlastne} onChange={e => setVlastne(e.target.value)} placeholder="0–10" inputMode="decimal"
        style={{ width: 64, padding: '6px 8px', border: `1px solid ${COLORS.line}`, borderRadius: 6, font: 'inherit', fontFamily: TYPO.mono }} />
      <span style={{ fontSize: TYPO.sizes.sm }}>%</span>
    </div>
  )
  const napoveda = 'Platí pre celú zákazku a pre každú partiu; bez zadania ide 100 %. Pri prenose do Miezd (Súpis › Podklady aj vyúčtovanie) sa výmera zníži o percento — v Mzdách, na výplatnom liste ani v podklade pre partiu sa percento neukazuje. Rozdiel je vo výdavkoch stavby ako kredit na výspravky; vypláca sa z neho ten, kto výspravky urobí. Keď sa percento raz zapne, platí do konca zákazky; zmeniť ho môže len Mirko svojím PIN-om (Mirko 20. 10. 2026).'
  if (k.zapnute && !menim) {
    return (
      <Sekcia nazov="Výmera na výplatu partiám" napoveda={napoveda} style={{ marginBottom: SPACE.md }}
        akcie={k.smie_menit ? <BtnGhost onClick={() => { setVolba(k.pct); setVlastne(PREDVOLBY.includes(k.pct) ? '' : String(k.pct)); setMenim(true) }}>Zmeniť (PIN Mirka)</BtnGhost> : null}>
        <div style={{ fontSize: TYPO.sizes.base }}><b>{pctTxt(k.pct)}</b> <span style={{ color: COLORS.muted }}>· zapnuté {datTxt(k.od)}{k.kto ? ' (' + k.kto + ')' : ''} · uzamknuté do konca zákazky</span></div>
      </Sekcia>
    )
  }
  return (
    <Sekcia nazov="Výmera na výplatu partiám" napoveda={napoveda} style={{ marginBottom: SPACE.md }}
      akcie={k.smie_menit ? (menim
        ? <><BtnGhost onClick={() => setMenim(false)}>Zrušiť</BtnGhost><BtnNav onClick={zmen}>Uložiť (PIN Mirka)</BtnNav></>
        : <BtnNav onClick={zapni} disabled={!platne}>Zapnúť do konca zákazky</BtnNav>) : null}>
      {k.smie_menit ? vyber : <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>100 % — percento nastavuje office.</div>}
    </Sekcia>
  )
}

// ── Výdavky › Kredit na výspravky ─────────────────────────────────────
export default function KreditVyspravky({ projectId }) {
  const [k, nacitaj, zakaz] = useKredit(projectId)
  const toast = useToast()
  const confirm = useConfirm()
  const pinGate = usePinGate()
  if (zakaz) return null
  const uvolni = async () => {
    const ok = await confirm({ title: 'Uvoľniť zvyšok kreditu stavbe?', confirmLabel: 'Ďalej — PIN Mirka',
      message: `Zvyšok ${SK.eur(k.zostava)} prestane byť v nákladoch zákazky a zvýši jej zisk. Schváli len Mirko svojím PIN-om.` })
    if (!ok) return
    const g = await pinGate('vyspravky_uvolnit', { suma: k.zostava, info: `Kredit na výspravky — zvyšok stavbe (${projectId})` })
    if (!g || !g.ok) return
    const r = await fetchAuth(`${API}/${encodeURIComponent(projectId)}/uvolnit`, { method: 'POST', headers: { 'X-Pin-Audit-Id': String(g.audit_id || '') } })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) { toast.error(d.error || 'Nepodarilo sa'); return }
    toast.success('Zvyšok kreditu uvoľnený stavbe.'); nacitaj()
  }
  return (
    <>
      <PercentoVyplaty projectId={projectId} onZmena={nacitaj} />
      {k && (k.zapnute || k.cerpane > 0 || k.vytvorene > 0) && (
        <Sekcia nazov="Kredit na výspravky" popis={k.zapnute ? pctTxt(k.pct) + ' z výmery partií' : ''} style={{ marginBottom: 0 }}
          napoveda="Kredit = rozdiel medzi výmerou zo súpisu (alebo vyúčtovania) a výmerou, ktorá sa platí partiám, krát sadzba. Čerpá sa riadkami v Mzdách na tejto zákazke označenými „výspravky“ — ktokoľvek ich urobí, aj o rok. Zostatok je v nákladoch zákazky (výdaje); zvyšok stavbe uvoľní len Mirko."
          akcie={k.zostava > 0 && k.smie_menit ? <BtnGhost onClick={uvolni}>Uvoľniť zvyšok stavbe (PIN Mirka)</BtnGhost> : null}>
          <KpiRow cols={4}>
            <KpiCard title="Vytvorené" color={COLORS.nav} value={SK.eur(k.vytvorene)} />
            <KpiCard title="Čerpané na výspravky" color={COLORS.muted} value={SK.eur(k.cerpane)} />
            <KpiCard title="Uvoľnené stavbe" color={COLORS.muted} value={SK.eur(k.uvolnene)} />
            <KpiCard title="Zostáva (v nákladoch)" color={COLORS.add} value={SK.eur(k.zostava)} highlight />
          </KpiRow>
          {k.neocenene_m2 > 0 && <div style={{ marginTop: SPACE.sm, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Bez sadzby: {String(k.neocenene_m2).replace('.', ',')} m² — kredit sa dopočíta, keď office v Mzdách zadá sadzbu.</div>}
          {(k.prenosy.length > 0 || k.cerpania.length > 0) && (
            <div style={{ marginTop: SPACE.sm, overflowX: 'auto' }}>
              <Table compact>
                <THead><TR><TH>Pohyb</TH><TH>Partia</TH><TH align="right">m²</TH><TH align="right">€</TH></TR></THead>
                <TBody>
                  {k.prenosy.map((x, i) => <TR key={'p' + i}><TD>Prenos {ymTxt(x.ym)}{x.stav === 'closed' ? ' · uzavreté' : ''}</TD><TD>{x.partia}</TD><TD align="right" mono>{String(x.m2).replace('.', ',')}</TD><TD align="right" mono>+{SK.eur(x.eur)}</TD></TR>)}
                  {k.cerpania.map((x, i) => <TR key={'c' + i}><TD>Výspravky {datTxt(x.datum)} — {x.popis}</TD><TD>{x.partia}</TD><TD align="right" mono>—</TD><TD align="right" mono>−{SK.eur(x.suma)}</TD></TR>)}
                </TBody>
              </Table>
            </div>
          )}
        </Sekcia>
      )}
    </>
  )
}

// ── Výmery › Podklad pre partiu ───────────────────────────────────────
export function PodkladPartiaTlacidlo({ projectId }) {
  const [open, setOpen] = useState(false)
  const [bloky, setBloky] = useState(null)
  const [vyber, setVyber] = useState('')
  const toast = useToast()
  const otvor = () => {
    if (!projectId) { toast.warn('Najprv vyber zákazku.'); return }
    setOpen(true); setBloky(null)
    fetchAuth(`${API}/${encodeURIComponent(projectId)}/podklady`).then(r => r.ok ? r.json() : []).then(j => { const b = Array.isArray(j) ? j : []; setBloky(b); setVyber(b[0] ? String(b[0].blok_id) : '') }).catch(() => setBloky([]))
  }
  return (
    <>
      <Btn onClick={otvor} title="Podklad výmery po miestnostiach pre partiu — výmera, ktorá sa partii platí (ako blok v Mzdách)">Podklad pre partiu</Btn>
      <Modal open={open} onClose={() => setOpen(false)} title="Podklad výmery pre partiu" width="min(520px, 95vw)" maxWidth={520}
        footer={<><BtnGhost onClick={() => setOpen(false)}>Zrušiť</BtnGhost><BtnNav disabled={!vyber} onClick={() => { stiahniPodkladPartia(vyber, toast); setOpen(false) }}>Vytvoriť podklad</BtnNav></>}>
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginBottom: SPACE.sm }}>Partia a mesiac — presne ako blok v Mzdách. Miestnosti, stropy a steny na hlavičkovom papieri; súčet sedí s Mzdami.</div>
        {bloky === null ? <div>Načítavam…</div> : !bloky.length ? <div>Na tejto zákazke zatiaľ nie je mzda prenesená zo Súpisu.</div> : (
          <select value={vyber} onChange={e => setVyber(e.target.value)} style={{ width: '100%', padding: '8px 9px', border: `1px solid ${COLORS.line}`, borderRadius: 6, font: 'inherit' }}>
            {bloky.map(b => <option key={b.blok_id} value={b.blok_id}>{(b.partia || '—') + (b.meno ? ' (' + b.meno + ')' : '') + ' — ' + ymTxt(b.ym) + (b.stav === 'closed' ? ' · uzavreté' : '')}</option>)}
          </select>
        )}
      </Modal>
    </>
  )
}
